import { useState, useCallback } from 'react';
import { parseFile, isSupportedFile } from '../services/file-parser';
import { fileStore, type ParsedFileData, type ParsedFileType } from '../services/file-store';

/**
 * Threshold in total cells — files with more cells than this
 * only send a preview to the LLM (the rest is queryable via SQL).
 */
const INLINE_CELL_THRESHOLD = 5000;

/** Max preview rows sent inline for large files. */
const PREVIEW_ROW_COUNT = 5;

export interface AttachmentMeta {
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  type: ParsedFileType;
  /** Spreadsheet sheet metadata (empty array for non-spreadsheets). */
  sheets: Array<{
    name: string;
    headers: string[];
    totalRows: number;
    totalColumns: number;
  }>;
  totalCells: number;
  /** Full spreadsheet content as text (small files only). */
  inlineContent?: string;
  /** First N rows per sheet as preview (large spreadsheets only). */
  previewRows?: Array<{
    sheetName: string;
    rows: Array<Record<string, unknown>>;
  }>;
  /** Text file content (for type === 'text'). */
  textContent?: string;
  /** Base64 image data WITHOUT the data URI prefix (for type === 'image'). */
  base64Data?: string;
}

export interface PendingAttachment {
  fileId: string;
  fileName: string;
  fileSize: number;
  status: 'parsing' | 'ready' | 'error';
  error?: string;
}

export function useFileAttachments() {
  const [pending, setPending] = useState<PendingAttachment[]>([]);

  /**
   * Add files for parsing. Accepts a FileList or File array.
   */
  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const supported = fileArray.filter((f) => isSupportedFile(f.name));

    if (supported.length === 0) return;

    // Add placeholder entries for each file
    const placeholders: PendingAttachment[] = supported.map((f) => ({
      fileId: crypto.randomUUID(),
      fileName: f.name,
      fileSize: f.size,
      status: 'parsing' as const,
    }));

    setPending((prev) => [...prev, ...placeholders]);

    // Parse each file
    for (let i = 0; i < supported.length; i++) {
      const file = supported[i];
      const placeholder = placeholders[i];

      try {
        const parsed = await parseFile(file);
        // Override the random ID with the one from parsing
        const finalData: ParsedFileData = {
          ...parsed,
          fileId: placeholder.fileId,
        };
        fileStore.add(finalData);

        setPending((prev) =>
          prev.map((p) =>
            p.fileId === placeholder.fileId
              ? { ...p, status: 'ready' as const }
              : p,
          ),
        );
      } catch (err: any) {
        setPending((prev) =>
          prev.map((p) =>
            p.fileId === placeholder.fileId
              ? { ...p, status: 'error' as const, error: err.message }
              : p,
          ),
        );
      }
    }
  }, []);

  /**
   * Remove a pending attachment.
   */
  const removeFile = useCallback((fileId: string) => {
    setPending((prev) => prev.filter((p) => p.fileId !== fileId));
    fileStore.remove(fileId);
  }, []);

  /**
   * Build attachment metadata for all ready files.
   * This is what gets sent with the chat message.
   */
  const getAttachmentMetadata = useCallback((): AttachmentMeta[] => {
    const readyFiles = pending.filter((p) => p.status === 'ready');
    return readyFiles.map((p) => {
      const data = fileStore.get(p.fileId);
      if (!data) return null;

      const meta: AttachmentMeta = {
        fileId: data.fileId,
        fileName: data.fileName,
        fileSize: data.fileSize,
        mimeType: data.mimeType,
        type: data.type,
        sheets: data.sheets.map((s) => ({
          name: s.name,
          headers: s.headers,
          totalRows: s.totalRows,
          totalColumns: s.totalColumns,
        })),
        totalCells: data.totalCells,
      };

      if (data.type === 'image') {
        // Strip the data URI prefix for display purposes
        const raw = data.base64Data ?? '';
        const commaIdx = raw.indexOf(',');
        meta.base64Data = commaIdx >= 0 ? raw.slice(commaIdx + 1) : raw;
      } else if (data.type === 'text') {
        meta.textContent = data.textContent;
      } else {
        // Spreadsheet — include inline/preview metadata
        if (data.totalCells < INLINE_CELL_THRESHOLD) {
          meta.inlineContent = sheetsToText(data);
        } else {
          meta.previewRows = data.sheets.map((s) => ({
            sheetName: s.name,
            rows: s.rows.slice(0, PREVIEW_ROW_COUNT),
          }));
        }
      }

      return meta;
    }).filter(Boolean) as AttachmentMeta[];
  }, [pending]);

  /**
   * Get the raw File objects for all ready attachments.
   * Used by the upload flow to send files via FormData.
   */
  const getRawFiles = useCallback((): File[] => {
    const readyFiles = pending.filter((p) => p.status === 'ready');
    return readyFiles
      .map((p) => fileStore.get(p.fileId)?.rawFile)
      .filter(Boolean) as File[];
  }, [pending]);

  /**
   * Clear all attachments (e.g., on session clear).
   */
  const clearAll = useCallback(() => {
    setPending([]);
    fileStore.clear();
  }, []);

  return {
    pending,
    addFiles,
    removeFile,
    getAttachmentMetadata,
    getRawFiles,
    clearAll,
    hasFiles: pending.some((p) => p.status === 'ready'),
  };
}

/**
 * Convert all sheets to a textual representation for inline content.
 */
function sheetsToText(data: ParsedFileData): string {
  return data.sheets
    .map((sheet) => {
      const header = sheet.headers.join('\t');
      const rows = sheet.rows
        .map((row) => sheet.headers.map((h) => String(row[h] ?? '')).join('\t'))
        .join('\n');
      const label = data.sheets.length > 1 ? `[Sheet: ${sheet.name}]\n` : '';
      return `${label}${header}\n${rows}`;
    })
    .join('\n\n');
}
